import React, { useEffect, useRef, useState, useCallback } from 'react';
import { BrowserMultiFormatReader, BarcodeFormat, DecodeHintType } from '@zxing/library';
import { ScanResult } from '../types';

interface ScannerProps {
  onScanSuccess: (result: ScanResult) => void;
  onScanError?: (error: Error) => void;
  fullscreen?: boolean;
}

const Scanner: React.FC<ScannerProps> = ({ onScanSuccess, onScanError, fullscreen = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const [zoomSupported, setZoomSupported] = useState<boolean>(false);
  const [zoomMin, setZoomMin] = useState<number>(1);
  const [zoomMax, setZoomMax] = useState<number>(1);
  const [zoomStep, setZoomStep] = useState<number>(0.1);
  const [zoom, setZoom] = useState<number | null>(null);
  const lastResultRef = useRef<{ text: string; time: number } | null>(null);
  const SUCCESS_COOLDOWN_MS = 1500;

  const startScanning = useCallback(async () => {
    if (!readerRef.current || !videoRef.current) return;
    
    try {
      setError(null);
      
      await readerRef.current.decodeFromConstraints(
        { video: { facingMode: 'environment' } },
        videoRef.current,
        (result, error) => {
          if (result) {
            const text = result.getText();
            const barcodeFormatEnum = result.getBarcodeFormat();
            const format = barcodeFormatEnum.toString();
            const formatCode = Number((barcodeFormatEnum as unknown) as number);
            // Debounce repeated identical results to avoid duplicate handling
            const now = Date.now();
            const isDuplicate =
              lastResultRef.current &&
              lastResultRef.current.text === text &&
              now - lastResultRef.current.time < SUCCESS_COOLDOWN_MS;
            if (!isDuplicate) {
              lastResultRef.current = { text, time: now };
              onScanSuccess({
                text,
                format,
                formatCode
              });
            }
          }
          
          if (error && onScanError) {
            onScanError(error);
          }
        }
      );
    } catch (err) {
      setError('Cannot access the camera. Check permissions.');
      if (onScanError && err instanceof Error) {
        onScanError(err);
      }
    }
  }, [onScanError, onScanSuccess, SUCCESS_COOLDOWN_MS]);

  useEffect(() => {
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.CODE_128,
      BarcodeFormat.DATA_MATRIX,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
    ]);

    readerRef.current = new BrowserMultiFormatReader(hints);

    // Check if camera permission has already been granted
    const checkCameraPermission = async () => {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
        if (permissionStatus.state === 'granted') {
          startScanning();
        } else if (permissionStatus.state === 'denied') {
          setError('Camera access is blocked. Change your browser settings to allow access.');
        } else {
          // If status is 'prompt', we wait for the user
          // Automatically start the scanner, even if status is 'prompt'
          startScanning();
        }

        permissionStatus.addEventListener('change', () => {
          if (permissionStatus.state === 'granted') {
            startScanning();
          }
        });
      } catch (err) {
        // If the permissions API is not available, attempt to start the scanner
        // and handle any error if the user denies access
        startScanning();
      }
    };

    checkCameraPermission();

    const currentVideo = videoRef.current;
    return () => {
      if (readerRef.current) {
        readerRef.current.reset();
      }
      const stream = (currentVideo?.srcObject as MediaStream) || null;
      if (stream) {
        stream.getTracks().forEach(t => {
          try {
            t.stop();
          } catch {
            // ignore
          }
        });
      }
    };
  }, [startScanning]);

  useEffect(() => {
    const setupZoom = () => {
      const stream = (videoRef.current?.srcObject as MediaStream) || null;
      const track = stream?.getVideoTracks?.()[0];
      if (!track || typeof track.getCapabilities !== 'function') return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const caps: any = track.getCapabilities();
      if (caps && typeof caps.zoom === 'object') {
        const min = typeof caps.zoom.min === 'number' ? caps.zoom.min : 1;
        const max = typeof caps.zoom.max === 'number' ? caps.zoom.max : 1;
        const step = typeof caps.zoom.step === 'number' ? caps.zoom.step : 0.1;
        setZoomSupported(max > min);
        setZoomMin(min);
        setZoomMax(max);
        setZoomStep(step);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const settings: any = typeof track.getSettings === 'function' ? track.getSettings() : {};
        const current = typeof settings.zoom === 'number' ? settings.zoom : min;
        setZoom(current);
      }
    };
    const v = videoRef.current;
    if (v) {
      v.onloadedmetadata = setupZoom;
    }
    return () => {
      if (v) v.onloadedmetadata = null;
    };
  }, []);

  const applyZoom = async (value: number) => {
    const stream = (videoRef.current?.srcObject as MediaStream) || null;
    const track = stream?.getVideoTracks?.()[0];
    if (!track || typeof (track as any).applyConstraints !== 'function') return;
    setZoom(value);
    try {
      // Try standard first
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (track as any).applyConstraints({ zoom: value });
    } catch {
      try {
        // Fallback to advanced block
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (track as any).applyConstraints({ advanced: [{ zoom: value }] });
      } catch {
        // ignore if unsupported
      }
    }
  };

  const normalizeZoom = (value: number) => {
    const min = zoomMin;
    const max = zoomMax;
    const step = zoomStep || 0.1;
    const clamped = Math.min(max, Math.max(min, value));
    const quantized = Math.round((clamped - min) / step) * step + min;
    return Number(quantized.toFixed(3));
  };

  // removed duplicate definition moved above

  return (
    <div className={`scanner ${fullscreen ? 'fullscreen' : ''}`}>
      {!fullscreen && <h2>Barcode scanner</h2>}
      
      <div className="video-container">
        <video ref={videoRef} className="scanner-video" />
        <div className="scan-overlay">
          <div className="scan-target">
            <span>Scan the product</span>
            {zoomSupported && (
              <div className="zoom-control">
                {(() => {
                  const eps = 1e-3;
                  const target1 = normalizeZoom(1);
                  const target2 = normalizeZoom(2);
                  const target4 = normalizeZoom(4);
                  const can1 = 1 <= zoomMax + eps;
                  const can2 = 2 <= zoomMax + eps;
                  const can4 = 4 <= zoomMax + eps;
                  const isActive = (t: number) => zoom !== null && Math.abs((zoom as number) - t) < Math.max(zoomStep, 0.1) / 2;
                  return (
                    <div className="zoom-buttons">
                      <button type="button" disabled={!can1} aria-pressed={isActive(target1)} onClick={() => applyZoom(target1)}>1x</button>
                      <button type="button" disabled={!can2} aria-pressed={isActive(target2)} onClick={() => applyZoom(target2)}>2x</button>
                      <button type="button" disabled={!can4} aria-pressed={isActive(target4)} onClick={() => applyZoom(target4)}>4x</button>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {error && <div className="error-message">{error}</div>}
    </div>
  );
};

export default Scanner; 